import React, { useRef, useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { Row, Form, Input, Button, Layout, Col, Spin } from 'antd';
import cryptr from 'cryptr';
import { CompanyLogo } from 'images';
import { omit } from 'utils/user';
import auth from 'services/login/auth';
import login from 'services/login/login';
import { getUserTypes } from 'services/settings/userType';
import { fetchMessages } from 'services/settings/message';
import openpgpService from 'services/encryption/openpgpService';
import { createSession, waitForClientId } from 'services/session/session';
import {
	LOGGEDIN_USER_DATA,
	ACCESS_MATRIX,
	CURRENT_LAB_LOCATION,
} from 'global_config/constant-global';
import URI from 'global_config/uri';
import { AlphaNumInput } from 'shared_components/pattern_input';
import Message from 'shared_components/message';
import { UserAccessContext } from 'context/userAccess';
import FIELD_RULES from './constants';

import './login.css';

const { Header, Content } = Layout;

function Login() {
	const formRef = useRef();
	const history = useHistory();
	const crypt = new cryptr(process.env.REACT_APP_CRYPTR_KEY);
	const { defineUserAccess } = useContext(UserAccessContext);
	const [loading, setLoading] = useState(false);

	React.useEffect(() => {
		const clientId = sessionStorage.getItem('clientId');
		if (!clientId) {
			console.log('No clientId found on login page, reconnecting WebSocket...');
			const wsService = require('services/websocket/WebSocketService').default;
			wsService.reconnect(process.env.REACT_APP_WEBSOCKET);
		}
	}, []);

	async function handleSubmit() {
		const { getFieldsValue } = formRef.current;

		const { username, password } = getFieldsValue();

		try {
			setLoading(true);
			// Encrypt password using OpenPGP before sending to API
			const encryptedPassword = await openpgpService.encrypt(password);
			// console.log('Encrypted Password:', encryptedPassword);
			const response = await login(username, encryptedPassword);

			// Handle response data robustly (using compatible syntax for React Scripts 2.x)
			const responseData = (response && response.data) || {};
			const userData = (responseData.RECORDS && responseData.RECORDS[0]) || responseData;
			const jwtToken = responseData.AUTHORIZATION;

			if (!userData) {
				throw new Error('Invalid user data received');
			}

			// Store JWT token if provided (Required for secondary API calls like getUserTypes)
			if (jwtToken) {
				sessionStorage.setItem('JWT_TOKEN', jwtToken);
			}


			// Check if password reset is required
			if (userData.requirePasswordChange || response.data.MESSAGE === 'Password Change Required') {
				Message.warning('Password reset required. Redirecting to change password...');
				// Redirect to change password page with user data and encrypted password
				setTimeout(() => {
					history.push('/change-password', {
						userData: userData,
						encryptedOldPassword: encryptedPassword,
					});
				}, 1500);
				return;
			}

			// Check if user type is active (Error resilient check)
			try {
				const userTypesResponse = await getUserTypes();
				const userTypes = (userTypesResponse && userTypesResponse.data) || [];
				// The Login API uses 'loginType' for the ID, while User Types API uses 'userTypeID'
				const currentTypeID = userData.loginType || userData.userTypeID;
				const userType = userTypes.find(type => type.userTypeID === currentTypeID);

				if (userType && userType.active === 0) {
					Message.info('Your user type is currently inactive. Please contact your administrator.');
					setLoading(false);
					return;
				}
			} catch (typeError) {
				console.error('Error verifying user type status:', typeError);
				// We continue login if user type check fails to avoid blocking users 
				// due to secondary metadata fetch issues, unless explicitly inactive.
			}

			const loggedinUserData = {
				...omit(userData, ['accessRights', 'labSections']),
				secret: crypt.encrypt(password),
			};
			// @ts-ignore
			const { accessRights, currentLabLocation, labSections } = userData;

			defineUserAccess({ accessMatrix: accessRights });

			sessionStorage.setItem(LOGGEDIN_USER_DATA, JSON.stringify(loggedinUserData));
			sessionStorage.setItem(CURRENT_LAB_LOCATION, JSON.stringify(currentLabLocation));
			sessionStorage.setItem('LAB_SECTIONS', JSON.stringify(labSections || []));
			// sessionStorage.setItem(ACCESS_MATRIX, crypt.encrypt(JSON.stringify(accessRights)));
			sessionStorage.setItem(ACCESS_MATRIX, JSON.stringify(accessRights));

			// Fetch messages after successful login
			fetchMessages();

			await waitForClientId(5000);
			await createSession('LOGIN');

			Message.success({ message: 'You are now successfully logged in!' });

			auth.authenticate();
			redirectPage();
		} catch (error) {
			if (error.response) {
				const { status, data } = error.response;

				// Handle different error scenarios
				if (status === 403) {
					// Account blocked
					if (data && data.MESSAGE) {
						Message.error(data.MESSAGE);
					} else {
						Message.error('Account is blocked. Please contact administrator');
					}
				} else if (status === 401) {
					// Invalid credentials
					if (data && data.MESSAGE) {
						Message.error(data.MESSAGE);
					} else {
						Message.error('Incorrect Username/Password');
					}
				} else {
					Message.error('Login failed. Please try again.');
				}
			} else {
				Message.error('Connection error. Please check your network.');
			}
		} finally {
			setLoading(false);
		}
	}

	function redirectPage() {
		// Redirect to current session module or to dashboard module
		const selectedSiderKey = sessionStorage.getItem('SELECTED_SIDER_KEY');
		const targetUri = Object.values(URI).find(item => item.key && item.key.toString() === selectedSiderKey);
		
		const selectedLink = (targetUri && targetUri.link) || (URI.dashboard && URI.dashboard.link) || '/';
		history.push(selectedLink);
	}

	return (
		<Layout>
			<Spin spinning={loading}>
				<Header style={{ borderBottom: 'none' }}>
					<Row>
						<Col span={24} />
					</Row>
				</Header>
				<Content>
					<div className="login-form">
						<div style={{ textAlign: 'center' }}>
							<img src={CompanyLogo} alt="logo" className="login-logo-image" />
						</div>
						<Form onFinish={handleSubmit} ref={formRef} layout="vertical">
							<Form.Item
								name="username"
								label="Username"
								className="login-input font12"
								rules={FIELD_RULES.Username}
								normalize={value => (value ? value.toUpperCase() : value)}
							>
								<AlphaNumInput maxLength={20} autoFocus style={{ textTransform: 'uppercase' }} />
							</Form.Item>
							<Form.Item name="password" label="Password" rules={FIELD_RULES.password}>
								<Input.Password type="password" maxLength={20} />
							</Form.Item>
							<Form.Item style={{ marginBottom: 0 }}>
								<Button type="primary" htmlType="submit" className="login-form-button" block>
									SIGN IN TO MY ACCOUNT
								</Button>
							</Form.Item>
						</Form>
					</div>
				</Content>
			</Spin>
		</Layout>
	);
}

export default Login;
